<template>
  <div>
    <PageTitle v-bind:title="'Search Lemmata'" class="mt-2 mb-4" />

    <b-container fluid="xxl" class="mt-2">
      <b-row class="mt-4">
        <b-col>
          <div>
            <h6>Search pattern</h6>
            <b-form-input
              placeholder="Pattern (lemma form, internal/external occurrence)"
              v-model="pattern"
              @keyup.enter="searchLemmata"
              size="m"
            ></b-form-input>
          </div>
        </b-col>
        <b-col>
          <div>
            <h6>&nbsp;</h6>
            <b-button
              :disabled="loading || !pattern.trim() || selectedFilters.length === 0"
              @click="searchLemmata"
              size="sm"
              class="mt-custom-form"
            >
              Search
              <b-icon icon="search" aria-hidden="true"></b-icon>
            </b-button>
            <b-button
              :disabled="loading"
              @click="resetSearch"
              size="sm"
              variant="outline-secondary"
              class="mt-custom-form ml-2"
            >
              Reset
            </b-button>
          </div>
        </b-col>
        <b-col>
          <div>
            <h6>Filter Languages</h6>
            <LanguageSelect
              :key="filterKey"
              :language="languages"
              :multiple="true"
              @select="selectLanguage"
            />
          </div>
        </b-col>

        <b-col>
          <div>
            <h6>Filter Entities</h6>
            <Select2
              :key="filterKey"
              v-model="selectedFilters"
              :options="['internal', 'external', 'lemma']"
              :settings="{multiple: true, width: '100%'}"
            />
          </div>
        </b-col>
      </b-row>

      <b-row class="mt-4">
        <b-col>
          <b-alert v-if="loadError" show variant="danger">
            {{ loadError }}
          </b-alert>
          <b-alert v-if="legacyWarning && !loadError" show variant="warning">
            The current export supports lemma-form matches only. Regenerate
            <code>lemma_fuzzy_search/</code> for internal and external occurrence search.
          </b-alert>
          <div class="d-flex justify-content-between align-items-center mb-2">
            <span v-if="hasSearched">
              {{ items.length }} result{{ items.length === 1 ? '' : 's' }}
            </span>
            <b-form-select
              v-if="hasSearched"
              v-model="perPage"
              :options="[25, 50, 100]"
              size="sm"
              class="per-page-select"
            />
          </div>
          <b-table
            class="table"
            ref="table"
            :fields="fields"
            :items="items"
            :busy="loading"
            :per-page="perPage"
            :current-page="currentPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-compare="sortCompare"
            :show-empty="hasSearched"
            empty-text="No matching lemmata found"
            :fixed="true"
            :hover="true"
            :small="false"
            :bordered="false"
            :borderless="false"
            head-variant="light"
          >
            <template #cell(lemma)="data">
              <a
                target="_blank"
                :href="`#/lemma/${data.item.lemmaId}`"
                v-html="data.item.lemma"
              />
            </template>
            <template #cell(article)="data">
              <a
                v-if="data.item.articleId"
                target="_blank"
                :href="`#/article/${data.item.articleId}`"
                v-html="data.item.article"
              />
            </template>
            <template #table-busy>
              <div class="text-center p-4">
                <b-spinner class="mr-2" small></b-spinner>
                Searching lemmata…
              </div>
            </template>
          </b-table>
          <b-pagination
            v-if="items.length > perPage"
            v-model="currentPage"
            :total-rows="items.length"
            :per-page="perPage"
            align="center"
          />
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<style scoped>
  * >>> .tdAction {
    width: 100px;
  }
  .per-page-select {
    width: 90px;
  }
</style>

<script>
  import { mapState } from 'vuex'
  import PageTitle from '@/components/PageTitle.vue'
  import LanguageSelect from '@/components/Main/LanguageSelect'

  export default {
    name: 'LemmaSearch',
    data(){
      return {
        fields: [
          { key: 'match', sortable: true },
          { key: 'entity', sortable: true },
          { key: 'lemma', sortable: true },
          { key: 'language', sortable: true },
          { key: 'article', sortable: true },
          { key: 'dictionary', sortable: true },
        ],
        items: [],
        languages: [],
        selectedFilters: ['internal', 'external', 'lemma'],
        pattern: '',
        loading: false,
        loadError: null,
        hasSearched: false,
        legacyWarning: false,
        currentPage: 1,
        perPage: 50,
        sortBy: 'lemma',
        sortDesc: false,
        requestSequence: 0,
        filterKey: 0,
        filterTimer: null,
      }
    },
    components: {
      PageTitle,
      LanguageSelect
    },
    computed: {
      ...mapState({
        userSettings: state => state.user.settings,
        dictionaries: state => state.common.dictionaries,
      }),
      tableAdditionalData(){
        return this.userSettings && Array.isArray(this.userSettings.dictionaries)
          ? this.userSettings.dictionaries
          : []
      },
      dictionaryAcronyms() {
        const selected = new Set(this.tableAdditionalData.map(String))
        return this.dictionaries
          .filter(dictionary => selected.has(String(dictionary.id)))
          .map(dictionary => dictionary.acronym)
      }
    },
    watch: {
      selectedFilters() {
        this.scheduleSearch()
      },
      perPage() {
        this.currentPage = 1
      },
    },
    beforeDestroy() {
      window.clearTimeout(this.filterTimer)
    },
    methods: {
      sortableValue(value) {
        return String(value || '')
          .replace(/<[^>]*>/g, '')
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .trim()
          .toLocaleLowerCase()
      },
      sortCompare(first, second, key) {
        const order = this.sortableValue(first[key]).localeCompare(
          this.sortableValue(second[key]),
          undefined,
          { numeric: true }
        )
        if (order) return order
        return String(first.lemmaId).localeCompare(String(second.lemmaId))
      },
      selectLanguage(langs) {
        this.languages = langs
        this.scheduleSearch()
      },
      scheduleSearch() {
        if (!this.hasSearched || !this.pattern.trim()) return
        window.clearTimeout(this.filterTimer)
        this.filterTimer = window.setTimeout(() => this.searchLemmata(), 250)
      },
      async searchLemmata(){
        if (!this.pattern.trim() || !this.selectedFilters.length) {
          this.items = []
          this.hasSearched = true
          this.currentPage = 1
          return
        }
        const requestSequence = ++this.requestSequence
        this.loading = true
        this.loadError = null
        this.legacyWarning = false
        try {
          const response = await this.$store.dispatch('fuzzySearchLemmata', {
            pattern: this.pattern,
            userDictionaries: this.tableAdditionalData,
            dictionaryAcronyms: this.dictionaryAcronyms,
            languages: this.languages,
            filters: this.selectedFilters,
          })
          if (requestSequence !== this.requestSequence) return
          this.items = response.data
          this.legacyWarning = response.legacyFallback
          this.hasSearched = true
          this.currentPage = 1
        } catch (error) {
          if (requestSequence !== this.requestSequence) return
          this.items = []
          this.hasSearched = true
          this.loadError = error.message || 'Lemma search could not be completed'
        } finally {
          if (requestSequence === this.requestSequence) this.loading = false
        }
      },
      resetSearch() {
        this.requestSequence++
        window.clearTimeout(this.filterTimer)
        this.pattern = ''
        this.languages = []
        this.selectedFilters = ['internal', 'external', 'lemma']
        this.items = []
        this.loadError = null
        this.hasSearched = false
        this.legacyWarning = false
        this.loading = false
        this.currentPage = 1
        this.sortBy = 'lemma'
        this.sortDesc = false
        this.filterKey++
      },
    },
  }
</script>
